import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTabs } from '../lib/tabs.js';

/**
 * The open-records bar, under the site header.
 *
 * Styling is ported from opioid-demo's `.page-tabs-bar` (docs/STYLING.md sanctions carrying
 * it over when a CMS feature needs it, which this is). The behaviour behind it is not the
 * demo's — see lib/tabs.tsx.
 *
 * There is no stored "active tab": which one is active is derived from the URL. A second
 * source of truth for "what am I looking at" is a desync waiting to happen, and the router
 * already answers it.
 */
export function RecordTabBar() {
  const { tabs, notice, closeTab, closeAll, dismissNotice } = useTabs();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <>
      {tabs.length > 0 ? (
        <div className="page-tabs-bar print-hide">
          {tabs.map((tab) => {
            const isActive = pathname === tab.path;

            return (
              <div key={tab.key} className={`page-tab${isActive ? ' active' : ''}`}>
                <Link to={tab.path} className="page-tab-link" title={tab.label}>
                  {tab.label}
                </Link>
                {/*
                  A button, not the demo's clickable span: closing a tab is an action, and it
                  has to be reachable and announced without a mouse.
                */}
                <button
                  type="button"
                  className="page-tab-close btn btn-link p-0 border-0"
                  aria-label={`Close ${tab.label}`}
                  onClick={() => {
                    const next = closeTab(tab.key);
                    // Only move if we just closed what was on screen; closing a background
                    // tab should not yank the reader somewhere else.
                    if (isActive && next) navigate(next);
                  }}
                >
                  &times;
                </button>
              </div>
            );
          })}

          <button
            type="button"
            className="page-tab-clear btn btn-link border-0"
            title="Close all tabs"
            onClick={() => {
              closeAll();
              navigate('/dashboard');
            }}
          >
            <i className="bi bi-x-circle me-1" aria-hidden="true" />
            Close all
          </button>
        </div>
      ) : null}

      {/*
        Non-blocking by construction: it announces itself politely, sits out of the way, and
        clears itself after four seconds. It never covers a control or takes focus.
      */}
      {notice ? (
        <div
          className="record-tab-notice alert alert-warning shadow-sm"
          role="status"
          aria-live="polite"
        >
          {notice}
          <button
            type="button"
            className="btn-close ms-3"
            aria-label="Dismiss"
            onClick={dismissNotice}
          />
        </div>
      ) : null}
    </>
  );
}
